import { useTranslations } from "next-intl";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import ThemeButton from "src/components/button/ThemeButton";
import InlineInput from "src/components/inline-input/InlineInput";
import * as EmailValidator from "email-validator";

interface ReviewerInformationFormProps {
  reviewerFullname: string;
  setReviewerFullname: Dispatch<SetStateAction<string>>;
  reviewerAge: string;
  setReviewerAge: Dispatch<SetStateAction<string>>;
  reviewerEmail: string;
  setReviewerEmail: Dispatch<SetStateAction<string>>;
  setReviewStep: Dispatch<SetStateAction<number>>;
}
interface ReviewerInformationFormErrors {
  reviewerFullname: string;
  reviewerEmail: string;
}

const ReviewerInformationForm = ({
  reviewerFullname,
  setReviewerFullname,
  reviewerAge,
  setReviewerAge,
  reviewerEmail,
  setReviewerEmail,
  setReviewStep,
}: ReviewerInformationFormProps) => {
  const t = useTranslations("ReviewForm");

  const [formErrors, setFormErrors] = useState<ReviewerInformationFormErrors>({
    reviewerFullname: "",
    reviewerEmail: "",
  });
  const [isFormSubmittedOnce, setIsFormSubmittedOnce] = useState(false);

  function validateForm(
    setFormErrors: Dispatch<SetStateAction<ReviewerInformationFormErrors>>,
    t: (string: string) => string
  ) {
    // full name
    let fullnameError = "";
    if (!reviewerFullname || reviewerFullname.trim().split(" ").length < 2) {
      fullnameError = t("fullname_is_required");
    }
    // email
    let emailError = "";
    if (!reviewerEmail) {
      emailError = t("email_is_required");
    } else if (!EmailValidator.validate(reviewerEmail)) {
      emailError = t("validate_email");
    }

    // setting the errors
    setFormErrors((prevState) => ({
      ...prevState,
      reviewerFullname: fullnameError,
      reviewerEmail: emailError,
    }));

    return ![fullnameError, emailError].some(Boolean);
  }

  const handleValidateAndSubmitInformation = () => {
    setIsFormSubmittedOnce(true);
    const isFormValid = validateForm(setFormErrors, t);

    if (!isFormValid) {
      return;
    } else {
      setReviewStep((prevstep) => prevstep + 1);
      return;
    }
  };

  useEffect(
    function validateInputsOnChange() {
      if (isFormSubmittedOnce) {
        validateForm(setFormErrors, t);
      }
    },
    [isFormSubmittedOnce, reviewerFullname, reviewerEmail]
  );

  return (
    <>
      <section className="m-auto flex flex-col items-center gap-10">
        <h2 className="text-center text-2xl font-semibold">
          {t("you_are_almost_there")}
        </h2>
        <div className="space-y-4">
          <div className="flex flex-col gap-4 lg:flex-row">
            <div className="flex w-full flex-col gap-4">
              <div className="space-y-2">
                <InlineInput
                  label={t("name_placeholder")}
                  value={reviewerFullname}
                  onChange={(event) => setReviewerFullname(event.target.value)}
                />
                {formErrors.reviewerFullname && (
                  <p className="error-message">{formErrors.reviewerFullname}</p>
                )}
              </div>
              <InlineInput
                label={t("age")}
                type="number"
                value={reviewerAge}
                onChange={(event) => setReviewerAge(event.target.value)}
              />
            </div>
            <div className="flex w-full flex-col gap-4">
              <div className="space-y-2">
                <InlineInput
                  label={t("email_placeholder")}
                  value={reviewerEmail}
                  onChange={(event) => setReviewerEmail(event.target.value)}
                />
                {formErrors.reviewerEmail && (
                  <p className="error-message">{formErrors.reviewerEmail}</p>
                )}
              </div>
              <ThemeButton
                onClick={handleValidateAndSubmitInformation}
                className="w-full text-sm lg:whitespace-nowrap"
              >
                <span>{t("preview_your_review")}</span>
              </ThemeButton>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            {t("your_review_is_anonymous")}
          </p>
        </div>
      </section>
    </>
  );
};

export default ReviewerInformationForm;
